import { locate, locateNode, getBox, locateNodeByClassName } from "@dhx/ts-common/html";
import { Id } from "@dhx/ts-common/types";
import { Grid, ProGrid, IGridConfig } from "@dhx/ts-grid";
import { collectionStore } from "./CollectionStore";
import { TreeCollection } from "./treecollection";
import { DragEvents, ITransferData, ICopyObject } from "./types";
import { isTreeCollection } from "./helpers";
import { uid } from "@dhx/ts-common/core";

function getPosition(e: MouseEvent) {
	const y = e.clientY;
	const element = locateNode(e);
	if (!element) {
		return null;
	}
	const treeLine: HTMLElement = element.childNodes[0] as HTMLElement;
	if (treeLine) {
		const { top, height } = treeLine.getBoundingClientRect();
		return (y - top) / height;
	}
}

function dragEventContent(element: HTMLElement, elements: NodeList, exhaustiveList = false) {
	const rect = element.getBoundingClientRect();
	const ghost = document.createElement("div");
	const clone = element.cloneNode(true) as HTMLElement;

	clone.style.width = rect.width + "px";
	clone.style.height = rect.height + "px";
	clone.style.maxHeight = rect.height + "px";
	clone.style.fontSize = window.getComputedStyle(element.parentElement).fontSize;
	clone.style.opacity = "0.8";
	clone.style.fontSize = window.getComputedStyle(element.parentElement).fontSize;

	if (!exhaustiveList || !elements || !elements.length) {
		ghost.appendChild(clone);
	}

	if (elements && elements.length) {
		elements.forEach((node, key) => {
			const nodeClone = node.cloneNode(true) as HTMLElement;
			nodeClone.style.width = rect.width + "px";
			nodeClone.style.height = rect.height + "px";
			nodeClone.style.maxHeight = rect.height + "px";
			nodeClone.style.top = (key + 1) * 12 - rect.height - rect.height * key + "px";
			nodeClone.style.left = (key + 1) * 12 + "px";
			nodeClone.style.opacity = "0.6";
			nodeClone.style.zIndex = `${-key - 1}`;
			ghost.appendChild(nodeClone);
		});
	}
	ghost.className = "dhx_drag-ghost";
	return ghost;
}

export interface IDragManager {
	setItem(id: Id, item: any): void;
	onMouseDown(
		e: MouseEvent | TouchEvent,
		source?: Id[],
		itemsForGhost?: NodeList | HTMLDivElement[] | Element[]
	): void;
	isDrag(): boolean;
	cancelCanDrop(event: MouseEvent | TouchEvent): void;
}

class DragManager implements IDragManager {
	private _transferData: ITransferData = {};
	private _itemsForGhost: NodeList;
	private _canMove = true;
	private _isDrag = false;

	public setItem(id: Id, item: any): void {
		collectionStore.setItem(id, item);
	}
	public onMouseDown(
		event: MouseEvent | TouchEvent,
		source?: Id[],
		itemsForGhost?: NodeList | HTMLDivElement[] | Element[]
	): void {
		// onmousedown only for target objects
		if (event.which !== 1 && !(event as TouchEvent).targetTouches) {
			return;
		}

		if (!(event as TouchEvent).targetTouches) {
			document.addEventListener("mousemove", this._onMouseMove as any);
			document.addEventListener("mouseup", this._onMouseUp as any);
		} else {
			document.addEventListener("touchmove", this._onMouseMove as any, false);
			document.addEventListener("touchend", this._onMouseUp as any, false);
		}

		const item = locateNode(event, "data-dhx-id") as HTMLElement;
		const id = item && item.getAttribute("data-dhx-id");
		const componentId = locate(event, "data-dhx-widget-id");

		if (Array.isArray(source) && source.includes(id)) {
			this._transferData.source = [...source];
			this._itemsForGhost = itemsForGhost as NodeList;
		} else {
			this._transferData.source = [id];
			this._itemsForGhost = null;
		}
		if (id && componentId) {
			const { left, top } = getBox(item);
			const pageX = (event as TouchEvent).targetTouches
				? (event as TouchEvent).targetTouches[0].pageX
				: (event as MouseEvent).pageX;
			const pageY = (event as TouchEvent).targetTouches
				? (event as TouchEvent).targetTouches[0].pageY
				: (event as MouseEvent).pageY;

			this._transferData.initXOffset = pageX - left;
			this._transferData.initYOffset = pageY - top;
			this._transferData.x = pageX;
			this._transferData.y = pageY;
			this._transferData.componentId = componentId;
			this._transferData.start = id;
			this._transferData.item = item;
		}
	}
	public isDrag(): boolean {
		return this._isDrag;
	}
	public cancelCanDrop(event: MouseEvent): void {
		this._canMove = false;
		this._isDrag = false;
		const { start, source, target, dropComponentId } = this._transferData;
		const data = {
			start,
			source,
			target,
		};
		const collection = collectionStore.getItem(dropComponentId);
		if (collection && target) {
			collection.events.fire(DragEvents.cancelDrop, [data, event]);
		}
		this._transferData.dropComponentId = null;
		this._transferData.target = null;
	}
	private _onMouseMove = (e: MouseEvent & TouchEvent) => {
		if (!this._transferData.start) {
			return;
		}

		const pageX = e.targetTouches ? e.targetTouches[0].pageX : e.pageX;
		const pageY = e.targetTouches ? e.targetTouches[0].pageY : e.pageY;

		const { x, y, start, componentId } = this._transferData;
		if (!this._transferData.ghost) {
			if (Math.abs(x - pageX) < 3 && Math.abs(y - pageY) < 3) {
				return;
			} else {
				const ghost = this._onDragStart(start, componentId, e);
				if (!ghost) {
					this._endDrop(e);
					return;
				} else {
					this._transferData.ghost = ghost;
					document.body.appendChild(this._transferData.ghost);
				}
			}
		}
		this._moveGhost(pageX, pageY);
		this._onDrag(e);
	};
	private _onMouseUp = (e: MouseEvent & TouchEvent) => {
		if (!this._transferData.x) {
			return;
		}
		if (this._transferData.ghost) {
			this._removeGhost();
			this._onDrop(e);
		} else {
			this._endDrop(e);
		}

		if (!e.targetTouches) {
			document.removeEventListener("mousemove", this._onMouseMove as any);
			document.removeEventListener("mouseup", this._onMouseUp as any);
		} else {
			document.removeEventListener("touchmove", this._onMouseMove as any);
			document.removeEventListener("touchend", this._onMouseUp as any);
		}
	};
	private _moveGhost(x: number, y: number) {
		if (this._transferData.ghost) {
			this._transferData.ghost.style.left = x - this._transferData.initXOffset + "px";
			this._transferData.ghost.style.top = y - this._transferData.initYOffset + "px";
		}
	}
	private _removeGhost() {
		document.body.removeChild(this._transferData.ghost);
	}
	private _onDrop(e: MouseEvent) {
		if (!this._canMove) {
			this._endDrop(e);
			return;
		}

		const { start, source, target, dropComponentId, dropPosition } = this._transferData;
		const data = { start, source, target, dropPosition };
		const component = collectionStore.getItem(dropComponentId);
		const config = component && component.config;

		if (!component || config.dragMode === "source") {
			this._endDrop(e);
			return;
		}
		if (component.events.fire(DragEvents.beforeDrop, [data, e])) {
			const to = {
				id: target,
				component,
			};
			const from = {
				id: start,
				component: this._transferData.component,
				newId: null,
			};
			this._move(from, to);
			if (from.newId && from.component !== to.component) data.start = from.newId;
			to.component.events.fire(DragEvents.afterDrop, [data, e]);
		}
		this._endDrop(e);
	}
	private _onDragStart(id: Id, componentId: Id, e: MouseEvent) {
		const component = collectionStore.getItem(componentId);
		const config = component.config;
		const { start, source, target } = this._transferData;
		const data = {
			start,
			source,
			target,
		};

		if (config.dragMode === "target" || component._pregroupData) {
			return null;
		}
		const ghost = dragEventContent(
			this._transferData.item,
			this._itemsForGhost,
			config.dragItem === "column" || config.dragItem === "both"
		);
		const ans = component.events.fire(DragEvents.beforeDrag, [data, e, ghost]);
		if (!ans || !id) {
			return null;
		}
		component.events.fire(DragEvents.dragStart, [data, e]);
		this._isDrag = true;
		this._toggleTextSelection(true);
		this._transferData.component = component;
		this._transferData.dragConfig = config;
		return ghost;
	}
	private _onDrag(e: MouseEvent & TouchEvent) {
		const clientX = e.targetTouches ? e.targetTouches[0].clientX : e.clientX;
		const clientY = e.targetTouches ? e.targetTouches[0].clientY : e.clientY;
		const element = document.elementFromPoint(clientX, clientY);

		const collectionId = locate(element, "data-dhx-widget-id");
		if (!collectionId) {
			if (this._canMove) {
				this.cancelCanDrop(e);
			}
			return;
		}

		const component = collectionStore.getItem(collectionId);
		const isTreeHeaderOrFooter =
			!!locateNodeByClassName(element, "dhx_grid-header") ||
			!!locateNodeByClassName(element, "dhx_grid-footer");
		const gridConfig =
			component && (component.config as IGridConfig).columns
				? (component.config as IGridConfig)
				: undefined;
		const isColumnDrag =
			gridConfig && (gridConfig.dragItem === "both" || gridConfig.dragItem === "column");

		if (isTreeHeaderOrFooter && !isColumnDrag) {
			if (this._canMove) {
				this.cancelCanDrop(e);
			}
			return;
		}

		const id = locate(element, "data-dhx-id");
		const rootId = locate(element, "data-dhx-root-id");

		if (!id && !rootId) {
			this.cancelCanDrop(e);
			this._transferData.dropComponentId = collectionId;
			this._transferData.target = null;
			this._canDrop(e);
			return;
		}

		const { dropComponentId, start, source, target, componentId, dropPosition } = this._transferData;

		if (component.config.dropBehaviour === "complex") {
			const pos = getPosition(e);
			if (pos <= 0.25) {
				this._transferData.dropPosition = "top";
			} else if (pos >= 0.75) {
				this._transferData.dropPosition = "bottom";
			} else {
				this._transferData.dropPosition = "in";
			}
		} else if ((target === id || (!id && target === rootId)) && dropComponentId === collectionId) {
			return;
		}

		const from: ICopyObject = {
			id: start,
			component: this._transferData.component,
		};
		if (component.config.dragMode === "source") {
			return;
		}

		from.component.events.fire(DragEvents.dragOut, [
			{
				start,
				source,
				target,
			},
			e,
		]);

		if (
			collectionId !== componentId ||
			!isTreeCollection(from.component.data) ||
			(isTreeCollection(from.component.data) && from.component.data.canCopy(from.id, id))
		) {
			this.cancelCanDrop(e); // clear last
			this._transferData.target = id || rootId;
			this._transferData.dropComponentId = collectionId;
			const canMove = from.component.events.fire(DragEvents.dragIn, [
				{
					start,
					source,
					target,
					dropPosition,
				},
				e,
			]);
			if (canMove) {
				this._canDrop(e);
			}
		} else {
			this.cancelCanDrop(e);
		}
	}
	private _move(from: ICopyObject, to: ICopyObject): void {
		const grid = (from.component as any) as Grid;
		const nextGrid = (to.component as any) as Grid;
		const fromData = from.component.data;
		const toData = to.component.data;
		let index = 0;
		let componentId = to.id;
		const behaviour = isTreeCollection(toData) ? to.component.config.dropBehaviour : undefined;

		const gridConfig = (from.component.config as IGridConfig).columns
			? (from.component.config as IGridConfig)
			: undefined;

		const isColumnDrag =
			gridConfig &&
			(gridConfig.dragItem === "both" || gridConfig.dragItem === "column") &&
			gridConfig.columns.map(c => c.id).filter(id => id === from.id || id === to.id).length;

		if (isColumnDrag && from.component === to.component) {
			if (from.id === to.id) return;
			const currentCols = grid.config.columns.map(c => ({ ...c }));
			const sourceIndex = currentCols.findIndex(c => c.id === from.id);
			const componentIndex = currentCols.findIndex(c => c.id === to.id);
			if (componentIndex === -1) return;
			currentCols.splice(componentIndex, 0, currentCols.splice(sourceIndex, 1)[0]);
			grid.setColumns(currentCols);
			grid.paint();
			return;
		} else if (isColumnDrag && from.component instanceof ProGrid && to.component instanceof ProGrid) {
			const currentCols = grid.config.columns.map(c => ({ ...c }));
			const sourceIndex = currentCols.findIndex(c => c.id === from.id);
			const nextGridCols = nextGrid.config.columns.map(c => ({ ...c }));
			const nextGridLength = nextGrid.data.getLength();
			const componentIndex = nextGridCols.findIndex(c => c.id === to.id);
			const currentColumnData = [];
			const currentColumnId = from.id;

			grid.data.forEach(item => {
				currentColumnData.push({ id: item.id, [currentColumnId]: item[from.id] });
			});
			if (nextGridLength) {
				grid.data.forEach((item, index) => {
					const nextGridItem = nextGrid.data.getItem(item.id);
					if (nextGridItem) {
						nextGrid.data.update(nextGridItem.id, {
							...nextGridItem,
							...currentColumnData[index],
						});
					} else {
						nextGrid.data.add(currentColumnData[index]);
					}
				});
			} else {
				nextGrid.data.parse(currentColumnData);
			}
			const col = currentCols.splice(sourceIndex, 1)[0];
			nextGridCols.find(c => c.id === currentColumnId) || nextGridCols.splice(componentIndex, 0, col);
			nextGrid.setColumns(nextGridCols);
			nextGrid.paint();
			grid.setColumns(currentCols);
			grid.paint();
			return;
		}

		const isRootParent = to.id === nextGrid.config.rootParent;

		switch (behaviour) {
			case "child":
				break;
			case "sibling":
				componentId = (toData as TreeCollection).getParent(componentId);
				index = toData.getIndex(to.id) + 1;
				break;
			case "complex": {
				const dropPosition = this._transferData.dropPosition;
				if (isRootParent) {
					componentId = to.id;
					index = toData.getLength();
				} else {
					if (dropPosition === "top") {
						componentId = (toData as TreeCollection).getParent(componentId);
						index = toData.getIndex(to.id);
					} else if (dropPosition === "bottom") {
						componentId = (toData as TreeCollection).getParent(componentId);
						index = toData.getIndex(to.id) + 1;
					}
				}
				break;
			}
			default:
				// list move
				if (!to.id) {
					index = -1;
				} else if (
					from.component === to.component &&
					toData.getIndex(from.id) < toData.getIndex(to.id)
				) {
					index = toData.getIndex(to.id) - 1;
				} else {
					if (toData.getIndex(from.id) > -1) from.newId = uid();
					index = toData.getIndex(to.id);
				}
		}

		if (this._transferData.dragConfig.dragCopy) {
			if (this._transferData.source instanceof Array && this._transferData.source.length > 1) {
				this._transferData.source.map(selctedId => {
					fromData.copy(selctedId, index, toData, componentId);
					if (index > -1) {
						index++;
					}
				});
			} else {
				fromData.copy(from.id, index, toData, componentId);
			}
		} else {
			if (this._transferData.source instanceof Array && this._transferData.source.length > 1) {
				this._transferData.source.map(selctedId => {
					(fromData as any).move(selctedId, index, toData, componentId);
					if (index > -1) {
						index++;
					}
				});
			} else {
				if (nextGrid instanceof Grid && !nextGrid.config.columns.length) {
					const gridItem = grid.data.getItem(from.id);
					nextGrid.data.parse([{ ...gridItem }]);
					nextGrid.setColumns([...grid.config.columns]);
					nextGrid.paint();
					grid.data.remove(from.id);
					grid.paint();
				} else {
					(fromData as any).move(from.id, index, toData, componentId, from.newId);
				}
			}
		}
	}
	private _endDrop(e: MouseEvent) {
		this._toggleTextSelection(false);
		if (this._transferData.component) {
			const { start, source, target } = this._transferData;
			const data = { start, source, target };
			this._transferData.component.events.fire(DragEvents.afterDrag, [data, e]);
		}
		this.cancelCanDrop(e);
		this._canMove = true;
		this._transferData = {};
		this._transferData.target = null;
		this._transferData.dropComponentId = null;
	}
	private _canDrop(e: MouseEvent) {
		this._canMove = true;
		const { start, source, target, dropPosition } = this._transferData;
		const data = {
			start,
			source,
			target,
			dropPosition,
		};

		const component = collectionStore.getItem(this._transferData.dropComponentId);
		if (component && this._transferData.target) {
			component.events.fire(DragEvents.canDrop, [data, e]);
		}
	}
	private _toggleTextSelection(add: boolean) {
		if (add) {
			document.body.classList.add("dhx_no-select");
		} else {
			document.body.classList.remove("dhx_no-select");
		}
	}
}

const dhx = ((window as any).dhxHelpers = (window as any).dhxHelpers || {});
dhx.dragManager = dhx.dragManager || new DragManager();
export const dragManager: IDragManager = dhx.dragManager;
